import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
    Smartphone, Shirt, Cpu, Tv, Briefcase,
    Search, Filter, ShoppingBag, PlusCircle, MapPin,
    ArrowRight, Tag, Info, X, Camera, Package,
    ChevronRight, Check, Upload, UserPlus, LayoutDashboard,
    Store, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "@/components/ui/dialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { PaymentModal } from "@/components/marketplace/PaymentModal";
import { Facebook, MessageCircle, Send, ShoppingCart, Trash2, CreditCard } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "https://bvglinkamazon.com") + "/api";

export const MarketplacePage = () => {
    const { addToCart, cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
    const totalItems = cart.reduce((acc, item) => acc + (item.quantity || 1), 0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [fullProducts, setFullProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showCartDrawer, setShowCartDrawer] = useState(false);
    const [sellers, setSellers] = useState([]);

    // Sell Modal State
    const [showSellModal, setShowSellModal] = useState(false);
    const [sellLoading, setSellLoading] = useState(false);
    const [sellFormData, setSellFormData] = useState({
        title: "",
        description: "",
        price_htg: "",
        category: "phones",
        type: "physical",
        condition: "new",
        stock: 1,
        images: [""]
    });

    // Payment/Product Detail Modal State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showPayModal, setShowPayModal] = useState(false);
    const [payLoading, setPayLoading] = useState(false);
    const [buyerPhone, setBuyerPhone] = useState("");

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [catRes, prodRes, sellRes] = await Promise.all([
                axios.get(`${API}/marketplace/categories`),
                axios.get(`${API}/marketplace/products?service=marketplace`),
                axios.get(`${API}/marketplace/sellers`)
            ]);
            const prodResData = prodRes.data || [];
            
            setCategories(catRes.data || []);
            setProducts(prodResData);
            setFullProducts(prodResData);
            setSellers(sellRes.data || []);

            // Check if there's a product shared link in URL
            const productIdFromUrl = searchParams.get("product");
            if (productIdFromUrl) {
                const targetProduct = prodResData.find(p => p.id === productIdFromUrl);
                if (targetProduct) {
                    setSelectedProduct(targetProduct);
                    setShowPayModal(true);
                }
            }
        } catch (error) {
            console.error("Erè nan chaje Marketplace:", error);
            toast.error("Pa kapab chaje Marketplace la pou kounye a.");
        } finally {
            setLoading(false);
        }
    };

    const handleSellSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("seller_token");
        if (!token) {
            toast.info("Tanpri konekte kòm machann pou w ka poste yon pwodwi.");
            return navigate("/seller/login");
        }

        if (!sellFormData.title || !sellFormData.price_htg || !sellFormData.description) {
            return toast.error("Tanpri ranpli tout jaden obligatwa yo.");
        }

        setSellLoading(true);
        try {
            const response = await axios.post(`${API}/marketplace/products`, {
                ...sellFormData,
                price_htg: parseFloat(sellFormData.price_htg)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success("Pwodwi ou poste avèk siksè!");
                setShowSellModal(false);
                setSellFormData({
                    title: "", description: "", price_htg: "", category: "phones",
                    type: "physical", condition: "new", stock: 1, images: [""]
                });
                fetchInitialData();
            }
        } catch (error) {
            console.error("Sell error:", error);
            toast.error(error.response?.data?.detail || "Erè nan poste pwodwi nan.");
        } finally {
            setSellLoading(false);
        }
    };

    const handlePurchase = async () => {
        if (!buyerPhone) {
            return toast.error("Tanpri antre nimewo Moncash ou.");
        }
        setPayLoading(true);
        try {
            const response = await axios.post(`${API}/marketplace/pay`, {
                product_id: selectedProduct.id,
                buyer_phone: buyerPhone
            });
            if (response.data.success && response.data.redirect_url) {
                window.location.href = response.data.redirect_url;
            }
        } catch (error) {
            console.error("Payment error:", error);
            toast.error(error.response?.data?.detail || "Erè nan prepare peman.");
        } finally {
            setPayLoading(false);
        }
    };

    const getDisplayProducts = () => {
        // Exclude POS-only products from the marketplace entirely
        let list = products.filter(p => {
            const displayOn = (p.display_on || "marketplace").toLowerCase();
            return displayOn !== "pos"; // keep 'marketplace', 'both', or unset
        });

        // If search query or active category exists, show products
        if (searchQuery || activeCategory) {
            if (activeCategory) {
                list = list.filter(p => p.category === activeCategory);
            }
            if (searchQuery) {
                return list.filter(p =>
                    p.title.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            return list;
        }

        // DEFAULT VIEW: Show nothing here, because we will show stores instead.
        return [];
    };

    const filteredProducts = getDisplayProducts();
    const showStores = !searchQuery && !activeCategory;

    const getCategoryIcon = (iconName) => {
        switch (iconName) {
            case "Smartphone": return <Smartphone className="w-5 h-5" />;
            case "Shirt": return <Shirt className="w-5 h-5" />;
            case "Cpu": return <Cpu className="w-5 h-5" />;
            case "Tv": return <Tv className="w-5 h-5" />;
            case "Briefcase": return <Briefcase className="w-5 h-5" />;
            default: return <ShoppingBag className="w-5 h-5" />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />

            {/* Seller Invite Banner - Only if not logged in as seller */}
            {!localStorage.getItem("seller_token") && (
                <div className="bg-[#FF9900]/10 border-b border-[#FF9900]/20 py-4 px-4">
                    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#232F3E] flex items-center justify-center text-[#FF9900] shrink-0 shadow-lg">
                                <UserPlus className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-sm font-black text-[#232F3E] leading-tightitalic tracking-tighter">Vre w vle vann pwodwi pa w?</p>
                                <p className="text-[9px] text-[#FF9900] font-black uppercase tracking-[0.2em] mt-0.5 leading-none">Kreye yon kont machann ofisyèl kounye a!</p>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate("/seller/register")}
                            className="bg-[#232F3E] hover:bg-black text-white font-black rounded-xl px-10 h-10 shadow-xl transition-all hover:scale-105 uppercase text-[10px] tracking-widest"
                        >
                            KREYE KONT MACHANN
                        </Button>
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <section className="bg-[#232F3E] text-white py-12 px-4 shadow-inner">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex justify-center mb-6">
                        <img src="/logo.png" alt="Marketplace BVGLink Biznis" className="h-20 lg:h-28 object-contain" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }} />
                    </div>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8 font-medium">
                        Depo ou nan tout peyi a! Achte oswa vann pwodwi fizik ak dijital an Ayiti.
                    </p>

                    <div className="max-w-xl mx-auto flex gap-2">
                        <div className="relative flex-1 group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-[#FF9900] transition-colors" />
                            <Input
                                className="pl-10 h-12 bg-white text-black rounded-xl border-none shadow-lg focus:ring-2 focus:ring-[#FF9900] font-bold"
                                placeholder="Chèche yon pwodwi..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <Button className="bg-[#FF9900] hover:bg-[#E88B00] h-12 px-8 rounded-xl font-black shadow-lg shadow-[#FF9900]/20 transition-all hover:scale-105">
                            Chèche
                        </Button>
                    </div>
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-4 py-8 flex-1">
                <div className="flex flex-col-reverse gap-12">
                    {/* Sidebar - Categories */ }
                    <aside className="w-full space-y-6">
                        <div className="bg-white p-6 md:p-8 rounded-[32px] shadow-sm border border-gray-100">
                            <h3 className="text-[12px] md:text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center justify-center md:justify-start gap-2">
                                <Filter className="w-5 h-5 text-[#FF9900]" /> Chwazi yon Kategori poun wè plis
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                <button
                                    onClick={() => setActiveCategory(null)}
                                    className={`w-full text-left px-5 py-4 rounded-2xl flex items-center gap-3 transition-all ${!activeCategory ? "bg-[#FF9900] text-white font-black shadow-lg shadow-[#FF9900]/20 scale-[1.02]" : "hover:bg-gray-50 text-gray-600 font-bold"}`}
                                >
                                    <ShoppingBag className="w-5 h-5" /> Tout Pwodwi
                                </button>
                                {categories.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setActiveCategory(cat.id)}
                                        className={`w-full text-left px-5 py-4 rounded-2xl flex items-center gap-3 transition-all ${activeCategory === cat.id ? "bg-[#FF9900] text-white font-black shadow-lg shadow-[#FF9900]/20 scale-[1.02]" : "hover:bg-gray-50 text-gray-600 font-bold"}`}
                                    >
                                        {getCategoryIcon(cat.icon)}
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Card className="bg-white border-2 border-gray-50 overflow-hidden rounded-[28px] shadow-sm hover:shadow-md transition-all max-w-md mx-auto">
                            <CardContent className="p-5 text-center relative">
                                <div className="absolute top-0 right-0 p-2 opacity-[0.03]">
                                    <LayoutDashboard className="w-12 h-12 text-[#232F3E]" />
                                </div>
                                <div className="w-10 h-10 bg-[#FF9900] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-[#FF9900]/20">
                                    <LayoutDashboard className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-base font-black mb-1 tracking-tighter uppercase leading-none italic text-gray-900">Dashboard <span className="text-[#FF9900]">Machann</span></h3>
                                <p className="text-[8px] text-gray-400 mb-5 font-black uppercase tracking-widest leading-none">Jere pwodwi ou kounye a.</p>
                                <Button
                                    onClick={() => {
                                        const token = localStorage.getItem("seller_token");
                                        if (!token) {
                                            toast.info("Tanpri konekte nan kont machann ou an.");
                                            navigate("/seller/login");
                                        } else {
                                            navigate("/seller/dashboard");
                                        }
                                    }}
                                    variant="outline"
                                    className="w-full font-black h-11 rounded-2xl bg-gray-50/50 text-[#232F3E] hover:bg-white border-2 border-gray-100 hover:border-[#FF9900]/30 shadow-sm text-[10px] tracking-[0.25em] uppercase transition-all flex items-center justify-between px-6"
                                >
                                    LOGIN MACHANN <ChevronRight className="w-5 h-5 text-[#FF9900]" />
                                </Button>
                            </CardContent>
                        </Card>
                    </aside>

                    {/* Main Content - Products */}
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="text-3xl font-black text-gray-900 leading-none mb-2 tracking-tighter italic">
                                    {showStores ? "Magazen" : activeCategory ? categories.find(c => c.id === activeCategory)?.name : "Tout Pwodwi yo"}
                                </h2>
                                <div className="h-1.5 w-16 bg-[#FF9900] rounded-full"></div>
                            </div>
                            <p className="bg-white px-5 py-2.5 rounded-2xl text-gray-400 text-[10px] font-black uppercase tracking-widest shadow-sm border border-gray-50">
                                {showStores ? `${sellers.length} magazen apwouve` : `${filteredProducts.length} pwodwi disponib`}
                            </p>
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                                {[1, 2, 3, 4, 5, 6].map(n => (
                                    <div key={n} className="bg-gray-200 h-80 rounded-[40px]" />
                                ))}
                            </div>
                        ) : showStores ? (
                            sellers.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {sellers.map((seller) => {
                                        // Count active products for this seller (optional but nice)
                                        const storeProductCount = products.filter(p => p.seller_id === seller.id).length;
                                        return (
                                            <Card
                                                key={seller.id}
                                                onClick={() => navigate(`/store/${seller.store_slug || seller.id}`)}
                                                className="rounded-[35px] overflow-hidden hover:shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-500 group border-none shadow-premium cursor-pointer bg-white flex flex-col h-full transform hover:-translate-y-2 relative"
                                            >
                                                <div className="h-48 w-full bg-[#232F3E] relative flex items-center justify-center overflow-hidden">
                                                    <div className="absolute inset-0 bg-[#FF9900]/10 mix-blend-overlay"></div>
                                                    {seller.logo_url ? (
                                                        <img src={seller.logo_url} alt={seller.store_name || seller.fullname} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90 group-hover:opacity-100" />
                                                    ) : (
                                                        <Store className="w-20 h-20 text-[#FF9900]/50 group-hover:bg-clip-text" />
                                                    )}
                                                    <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-black/80 to-transparent"></div>
                                                    <span className="absolute bottom-4 left-4 text-white font-black uppercase text-[10px] tracking-widest bg-black/40 backdrop-blur px-3 py-1.5 rounded-xl border border-white/10">
                                                        {storeProductCount} ATIK
                                                    </span>
                                                </div>
                                                <CardContent className="p-6 relative flex flex-col flex-1">
                                                    <div className="absolute -top-12 right-6 w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center uppercase font-black text-2xl text-[#FF9900] border border-gray-100 group-hover:rotate-12 transition-transform duration-500 overflow-hidden">
                                                        {seller.logo_url ? <img src={seller.logo_url} className="w-full h-full object-cover" /> : (seller.store_name || seller.fullname)?.[0]}
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <ShieldCheck className="w-4 h-4 text-green-500" />
                                                        <span className="text-[9px] font-black uppercase tracking-widest text-green-500 bg-green-50 px-2 py-1 rounded-lg">Verifye</span>
                                                    </div>
                                                    
                                                    <h3 className="text-xl font-black text-gray-900 group-hover:text-[#FF9900] transition-colors leading-tight mb-2 uppercase tracking-tighter">
                                                        {seller.store_name || seller.fullname}
                                                    </h3>
                                                    
                                                    <div className="flex items-center gap-2 text-xs font-bold text-gray-400 mt-auto pt-4 border-t border-gray-50">
                                                        <MapPin className="w-3.5 h-3.5 text-[#FF9900]" /> {seller.address || "Ayiti"}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-20">
                                    <Store className="w-16 h-16 text-gray-200 mx-auto mb-6" />
                                    <h3 className="text-2xl font-black text-gray-900 mb-2 italic">Poko gen magazen sou sistèm nan</h3>
                                    <p className="text-gray-400 text-xs font-black uppercase tracking-widest">Tounen pi ta pou w jwenn bon machann.</p>
                                </div>
                            )
                        ) : filteredProducts.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                                {filteredProducts.map(product => (
                                    <Card
                                        key={product.id}
                                        onClick={() => {
                                            setSelectedProduct(product);
                                            setShowPayModal(true);
                                        }}
                                        className="rounded-[28px] overflow-hidden hover:shadow-xl transition-all group border border-gray-100 shadow-sm cursor-pointer bg-white flex flex-col h-full"
                                    >
                                        <div className="h-[250px] md:h-[300px] w-full bg-gray-50 flex items-center justify-center relative overflow-hidden shrink-0 group-hover:bg-gray-100 transition-colors duration-500">
                                            {product.images?.[0] ? (
                                                <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 shadow-sm" />
                                            ) : (
                                                <Package className="w-16 h-16 text-gray-200" />
                                            )}
                                            <div className="absolute top-6 left-6 flex flex-col gap-2">
                                                <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-xl ${product.type === 'digital' ? 'bg-blue-600 text-white' : 'bg-[#232F3E] text-white'}`}>
                                                    {product.type}
                                                </span>
                                                {product.condition === 'new' && (
                                                    <span className="bg-[#FF9900] text-white px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-xl">NEW</span>
                                                )}
                                            </div>
                                        </div>
                                        <CardContent className="p-5 md:p-6 flex flex-col flex-1">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2 text-[10px] font-black text-[#FF9900] uppercase tracking-widest">
                                                    {getCategoryIcon(product.category)} {categories.find(c => c.id === product.category)?.name}
                                                </div>
                                                <div className="flex items-center gap-1 text-[9px] font-black text-gray-400 bg-gray-50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-gray-100">
                                                    <MapPin className="w-3 h-3 text-gray-400" /> {product.location || "Ayiti"}
                                                </div>
                                            </div>
                                            <h3 className="font-black text-lg mb-4 text-gray-900 group-hover:text-[#FF9900] transition-colors leading-tight tracking-tighter line-clamp-2">{product.title}</h3>
                                            {product.description && (
                                                <div className="mb-3 border-b border-gray-50 pb-3">
                                                    <p className="text-[11px] text-gray-500 font-medium max-h-[60px] overflow-y-auto custom-scrollbar leading-relaxed pr-2">{product.description}</p>
                                                </div>
                                            )}
                                            <div className="flex flex-col gap-3 mt-auto">
                                                <div className="flex flex-col mb-1">
                                                    <span className="text-2xl font-black text-gray-900 leading-none">{product.price_htg.toLocaleString()} <span className="text-[10px] font-black text-gray-400 relative -top-1">HTG</span></span>
                                                </div>
                                                <div className="flex gap-3 w-full">
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addToCart(product);
                                                        }}
                                                        className="flex-1 h-14 rounded-2xl bg-black text-white hover:bg-gray-800 shadow-2xl shadow-black/20 font-black tracking-widest uppercase transition-transform hover:scale-[1.02]"
                                                    >
                                                        <ShoppingBag className="w-5 h-5 mr-2" /> Ajoute
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const productLink = `${window.location.origin}/marketplace?product=${product.id}`;
                                                            window.open(`https://wa.me/?text=${encodeURIComponent("Tcheke sa sou BVGLink: " + product.title + " " + productLink)}`, "_blank");
                                                        }}
                                                        className="w-14 h-14 rounded-2xl border-gray-200 text-green-600 hover:bg-green-50 hover:border-green-200 shadow-sm transition-transform hover:scale-105 shrink-0"
                                                    >
                                                        <MessageCircle className="w-6 h-6" />
                                                    </Button>
                                                </div>
                                                <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-5 mt-1 overflow-hidden">
                                                    <div className="flex items-center gap-2 overflow-hidden flex-1">
                                                        <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-[10px] font-black text-gray-600 uppercase shadow-inner">{product.seller_name?.[0] || 'M'}</div>
                                                        <div className="flex flex-col overflow-hidden">
                                                            <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none mb-0.5">Machann</p>
                                                            <p className="text-[10px] font-black text-gray-700 uppercase tracking-tighter truncate leading-tight">{product.seller_name}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/store/${product.seller_slug || product.seller_id}`);
                                                            }}
                                                            variant="ghost"
                                                            className="h-10 px-3 shrink-0 text-[8px] font-black text-[#FF9900] hover:bg-[#FF9900]/10 uppercase tracking-widest rounded-xl border border-[#FF9900]/20"
                                                        >
                                                            MAGAZEN <ArrowRight className="ml-1 w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-[60px] p-24 text-center border-2 border-dashed border-gray-100 shadow-sm">
                                <div className="w-24 h-24 bg-gray-50 rounded-[35px] flex items-center justify-center mx-auto mb-10 text-gray-200">
                                    <ShoppingBag className="w-12 h-12" />
                                </div>
                                <h3 className="text-3xl font-black text-gray-900 mb-4 tracking-tighter italic">Plis pwodwi ap vini an wout...</h3>
                                <p className="text-gray-400 mb-10 max-w-sm mx-auto leading-relaxed font-bold uppercase text-[10px] tracking-[0.2em]">
                                    Nou poko gen pwodwi nan kategori sa a. Vann pwodwi pa w kounye a!
                                </p>
                                <Button
                                    onClick={() => {
                                        const token = localStorage.getItem("seller_token");
                                        if (!token) navigate("/seller/login");
                                        else navigate("/seller/dashboard");
                                    }}
                                    className="bg-[#232F3E] hover:bg-black text-[#FF9900] font-black h-16 px-12 rounded-[25px] shadow-2xl shadow-[#232F3E]/20 hover:scale-105 transition-all text-lg tracking-tight uppercase border-2 border-[#FF9900]/20"
                                >
                                    VINI PREMYE MACHANN NAN <PlusCircle className="ml-3 w-6 h-6 text-[#FF9900]" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Payment Modal */}
            {showPayModal && selectedProduct && (
                <PaymentModal
                    product={{ ...selectedProduct, name: selectedProduct.title }}
                    onClose={() => {
                        setShowPayModal(false);
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete("product");
                        navigate(`/marketplace?${newParams.toString()}`, { replace: true });
                    }}
                />
            )}

            {/* Floating Cart Button */}
            {totalItems > 0 && (
                <div className="fixed bottom-8 right-8 z-50 animate-in slide-in-from-bottom duration-300">
                    <Button
                        onClick={() => setShowCartDrawer(true)}
                        className="w-20 h-20 rounded-[30px] bg-[#232F3E] text-[#FF9900] shadow-2xl hover:scale-110 transition-all relative group"
                    >
                        <ShoppingCart className="w-10 h-10 group-hover:rotate-12 transition-transform" />
                        <span className="absolute -top-3 -right-3 bg-[#FF9900] text-black w-9 h-9 rounded-full flex items-center justify-center text-sm font-black border-4 border-[#232F3E] shadow-xl transform group-hover:scale-110 transition-transform">
                            {totalItems}
                        </span>
                    </Button>
                </div>
            )}

            {/* Cart Drawer Modal */}
            <Dialog open={showCartDrawer} onOpenChange={setShowCartDrawer}>
                <DialogContent className="sm:max-w-[500px] p-0 rounded-[45px] overflow-hidden border-none shadow-[0_0_100px_rgba(35,47,62,0.2)]">
                    <DialogHeader className="bg-[#232F3E] p-10 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5">
                            <ShoppingCart className="w-32 h-32 rotate-12" />
                        </div>
                        <DialogTitle className="text-3xl font-black italic tracking-tighter flex items-center gap-4 leading-none z-10">
                            <div className="p-3 bg-[#FF9900] rounded-2xl">
                                <ShoppingCart className="w-6 h-6 text-black" />
                            </div>
                            PANYE <span className="text-[#FF9900]">ACHAT</span>
                        </DialogTitle>
                        <DialogDescription className="text-gray-400 text-[10px] font-black uppercase tracking-[0.3em] mt-3 z-10">
                            OU GEN {totalItems} PWODWI NAN LIS OU A
                        </DialogDescription>
                    </DialogHeader>

                    <div className="p-8 max-h-[50vh] overflow-y-auto space-y-6 bg-white custom-scrollbar">
                        {totalItems === 0 ? (
                            <div className="text-center py-20 space-y-6">
                                <div className="w-24 h-24 bg-gray-50 rounded-[40px] flex items-center justify-center mx-auto text-gray-200 border-2 border-dashed border-gray-100">
                                    <ShoppingBag className="w-10 h-10" />
                                </div>
                                <div>
                                    <p className="text-xl font-black text-[#232F3E] tracking-tighter italic">Panye a vid nèt!</p>
                                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest mt-2">Kòmanse chwazi bèl pwodwi kounye a pwofese!</p>
                                </div>
                            </div>
                        ) : (
                            cart.map(item => (
                                <div key={item.id} className="flex gap-5 p-5 bg-gray-50 rounded-[32px] border border-gray-100 items-center hover:bg-white transition-colors group">
                                    <div className="w-20 h-20 rounded-[22px] overflow-hidden bg-white border border-gray-100 flex items-center justify-center p-3 shadow-sm group-hover:shadow-md transition-all">
                                        {item.images?.[0] ? <img src={item.images[0]} alt={item.title} className="w-full h-full object-contain" /> : <Package className="text-gray-200" />}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-black text-base text-[#232F3E] tracking-tighter line-clamp-1 mb-1">{item.title}</h4>
                                        <div className="flex items-center gap-4 mt-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#FF9900] font-black text-lg">{(item.price_htg * (item.quantity || 1)).toLocaleString()}</span>
                                                <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">HTG</span>
                                            </div>
                                            <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 p-1">
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-black" onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)} disabled={(item.quantity || 1) <= 1}>-</Button>
                                                <span className="text-xs font-black text-gray-900 w-4 text-center">{item.quantity || 1}</span>
                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-black" onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}>+</Button>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeFromCart(item.id)}
                                        className="w-12 h-12 rounded-2xl text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                    >
                                        <Trash2 className="w-6 h-6" />
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="p-10 border-t border-gray-50 bg-gray-50/50 space-y-8">
                        <div className="space-y-4 px-2 w-full">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Sou-Total:</span>
                                <span className="text-xl font-black text-gray-700 tracking-tighter">{cartTotal.toLocaleString()} HTG</span>
                            </div>
                            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Frè Sistèm (8%):</span>
                                <span className="text-xl font-black text-[#FF9900] tracking-tighter">+{Math.ceil(cartTotal * 0.08).toLocaleString()} HTG</span>
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <span className="text-[12px] font-black text-[#232F3E] uppercase tracking-[0.2em]">Total Nèt:</span>
                                <div className="text-right flex flex-col items-end">
                                    <span className="text-4xl font-black text-[#232F3E] tracking-tighter italic leading-none">{Math.ceil(cartTotal * 1.08).toLocaleString()}</span>
                                    <span className="text-[10px] font-black text-[#FF9900] uppercase tracking-widest mt-1">Goud Ayisyen (HTG)</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                onClick={clearCart}
                                className="h-16 rounded-[25px] font-black px-8 border-2 border-gray-200 hover:bg-white text-gray-300 hover:text-red-500 hover:border-red-200 text-[10px] tracking-widest uppercase transition-all"
                            >
                                VIDÉ
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowCartDrawer(false);
                                    if (totalItems > 0) {
                                        setSelectedProduct(cart[0]);
                                        setShowPayModal(true);
                                    }
                                }}
                                disabled={totalItems === 0}
                                className="flex-1 h-16 bg-[#232F3E] hover:bg-black text-[#FF9900] font-black rounded-[25px] shadow-2xl shadow-[#232F3E]/20 text-xs tracking-[0.25em] uppercase hover:scale-[1.02] transition-all flex items-center justify-center gap-4"
                            >
                                <CreditCard className="w-6 h-6" /> PASSE NAN PEYE
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
};

const Label = ({ children, className }) => (
    <label className={`block ${className}`}>{children}</label>
);

const Loader2 = ({ className }) => (
    <span className={`${className} animate-spin`}>⏳</span>
);

const PackagePlaceholder = ({ className }) => (
    <Package className={className} />
);
